import React, { createContext, useState, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import authService from "../../routes/authService";
import employeeDataService from "../Navbar/employeeService";
import permissionService from "../../Pages/Permissions/permissionService";
import { isPublicAppRoute } from "../../routes/axiosAuthUtils";

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(null);
  const [userCompanyInfo, setUserCompanyInfo] = useState(null);
  const [userDescriptionInfo, setUserDescriptionInfo] = useState(null);
  const [userNameFromEmployees, setUserNameFromEmployees] = useState(null);
  const [features, setFeatures] = useState([]);
  const [isLoadingUser, setIsLoadingUser] = useState(true);

  const loadUserData = async ({ force = false } = {}) => {
    setIsLoadingUser(true);

    // Rotas públicas não precisam carregar sessão/permissões,
    // exceto quando o login acabou de salvar o token (force=true).
    if (!force && isPublicAppRoute(location.pathname)) {
      setCurrentUser(null);
      setUserCompanyInfo(null);
      setUserDescriptionInfo(null);
      setUserNameFromEmployees(null);
      setFeatures([]);
      setIsLoadingUser(false);
      return;
    }

    const user = authService.getCurrentUser();
    setCurrentUser(user);

    if (user && user.login) {
      try {
        const [me, mine] = await Promise.all([
          employeeDataService.getMe(),
          permissionService.getMine(),
        ]);

        setUserCompanyInfo(me?.company?.description || null);
        setUserDescriptionInfo(me?.role?.description || null);
        setUserNameFromEmployees(me?.name || null);
        setFeatures(Array.isArray(mine?.features) ? mine.features : []);
      } catch (error) {
        console.error(
          "[UserContext] Erro ao carregar perfil/permissões:",
          error
        );
        setUserCompanyInfo(null);
        setUserDescriptionInfo(null);
        setUserNameFromEmployees(null);
        setFeatures([]);
      }
    } else {
      setUserCompanyInfo(null);
      setUserDescriptionInfo(null);
      setUserNameFromEmployees(null);
      setFeatures([]);
    }
    setIsLoadingUser(false);
  };

  useEffect(() => {
    loadUserData();
  }, [location.pathname]);

  const refreshUserData = async () => {
    await loadUserData({ force: true });
  };

  const hasFeature = (feature) =>
    features.map((item) => String(item).toUpperCase()).includes(String(feature).toUpperCase());

  return (
    <UserContext.Provider
      value={{
        currentUser,
        userCompanyInfo,
        userDescriptionInfo,
        userNameFromEmployees,
        features,
        hasFeature,
        isLoadingUser,
        refreshUserData,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
